import React, { useCallback, useState, useEffect } from "react";
import styled from "styled-components";
import { Button, TextInput } from "./components";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Wrapper = styled.div`
  flex-direction: column;
  align-items: stretch;
  max-width: 40em;
  margin: auto;
  padding: 4em;
  flex: 1 1 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5em 0;
  background: var(--linear, linear-gradient(136deg, #44276e 0%, #733c8f 100%));
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderLogo = styled.img`
  max-width: 13em;
  color: "#ffffff";
`;

const Footer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  padding: 1.5em 0;
  margin-top: 3em;
`;

const FooterText = styled.span`
  margin-left: 0.8em;
  font-size: 0.9em;
`;

const FooterLogo = styled.img`
  max-height: 1em;
  color: "#fff";
  margin: 0 0.3em;
  transition: max-height 0.2s;
  &:hover {
    max-height: 1.5em;
  }
`;

const WelcomeMessage = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.5em;
  padding: 1.5em;
  margin-bottom: 2em;
  line-height: 1.5;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 2em;
`;

const SuccessTitle = styled.h2`
  color: #28a745;
  margin-bottom: 1em;
`;

const SuccessMessage = styled.p`
  margin-bottom: 2em;
  line-height: 1.5;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  max-width: 20em;
  margin: 0 auto;
`;

const ActionButton = styled.a`
  display: inline-block;
  text-decoration: none;
  color: #ffffff;
  padding: 0.8em 1.5em;
  border-radius: 1.5em;
  border: none;
  cursor: pointer;
  min-width: 10em;
  font-size: 1em;
  font-weight: 600;
  text-align: center;
  transition: background-color 0.2s;

  &.primary {
    background-color: #733c8f;
    &:hover {
      background-color: #331756;
    }
  }

  &.secondary {
    background-color: #e21b54;
    &:hover {
      background-color: #ad1843;
    }
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1em;
`;

const FieldWrapper = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5em;
  cursor: pointer;
`;

const RadioInput = styled.input`
  margin: 0;
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5em;
  cursor: pointer;
  margin-top: 1em;
`;

const CheckboxInput = styled.input`
  margin: 0;
  margin-top: 0.2em;
`;

const CheckboxText = styled.span`
  font-size: 0.9em;
`;

const Spinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 0.9em;
  margin-top: 0.5em;
`;

const InfoMessage = styled.div`
  color: #666;
  font-size: 0.8em;
  margin-top: 0.2em;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1em;
  margin-top: 2em;
`;

interface FormData {
  userType: "parent" | "other" | "";
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  postalCode: string;
  acceptDataTreatment: boolean;
  engagement: boolean;
}

// Helper function to convert ECDSA signature from DER (Node.js) to P1363 (Web Crypto API)
const derToP1363 = (derSignature: Uint8Array): Uint8Array => {
  // Parse DER format: 0x30 [total-length] 0x02 [R-length] [R] 0x02 [S-length] [S]
  let offset = 0;

  // Skip SEQUENCE tag (0x30) and length
  offset += 2;

  // Read R
  if (derSignature[offset] !== 0x02) throw new Error("Invalid DER signature");
  offset++;
  const rLength = derSignature[offset++];
  let r = derSignature.slice(offset, offset + rLength);
  offset += rLength;

  // Read S
  if (derSignature[offset] !== 0x02) throw new Error("Invalid DER signature");
  offset++;
  const sLength = derSignature[offset++];
  let s = derSignature.slice(offset, offset + sLength);

  // Remove leading zeros if present (DER can have them)
  while (r.length > 32 && r[0] === 0) r = r.slice(1);
  while (s.length > 32 && s[0] === 0) s = s.slice(1);

  // Pad with leading zeros if needed (P1363 requires fixed 32-byte length)
  const rPadded = new Uint8Array(32);
  const sPadded = new Uint8Array(32);
  rPadded.set(r, 32 - r.length);
  sPadded.set(s, 32 - s.length);

  // Concatenate r and s for P1363 format
  const p1363Signature = new Uint8Array(64);
  p1363Signature.set(rPadded, 0);
  p1363Signature.set(sPadded, 32);

  return p1363Signature;
};

// Helper function to decode and verify email ECDSA signature using Web Crypto API
const verifyEmailSignature = async (
  signedEmail: string
): Promise<string | null> => {
  try {
    // Clé publique ECDSA P-256
    const publicKeyPem = `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEXvdKS/cspWmcZDr5eFB0VRPkMuQ7
rN6Eoa52MESdJW9YtfaGQLZcF4MJlpjSVUdP+SmzA8x0ElR8a2YraXS9aw==
-----END PUBLIC KEY-----`;

    // Décoder le base64 URL-safe
    const decoded = atob(
      signedEmail
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(signedEmail.length + ((4 - (signedEmail.length % 4)) % 4), "=")
    );

    const parts = decoded.split("|");

    if (parts.length !== 2) {
      console.error("Invalid signed email format");
      return null;
    }

    const [email, signature] = parts;

    // Importer la clé publique ECDSA
    const pemHeader = "-----BEGIN PUBLIC KEY-----";
    const pemFooter = "-----END PUBLIC KEY-----";
    const pemContents = publicKeyPem
      .replace(pemHeader, "")
      .replace(pemFooter, "")
      .replace(/\s/g, "");
    const binaryDer = atob(pemContents);
    const binaryArray = new Uint8Array(binaryDer.length);
    for (let i = 0; i < binaryDer.length; i++) {
      binaryArray[i] = binaryDer.charCodeAt(i);
    }

    const publicKey = await crypto.subtle.importKey(
      "spki",
      binaryArray,
      {
        name: "ECDSA",
        namedCurve: "P-256",
      },
      false,
      ["verify"]
    );

    // Préparer les données à vérifier
    const emailData = new TextEncoder().encode(email);

    // Convertir la signature DER (Node.js) vers P1363 (Web Crypto API)
    const signatureDer = new Uint8Array(
      atob(signature)
        .split("")
        .map((c) => c.charCodeAt(0))
    );

    // Parser la signature DER pour extraire r et s
    const signatureP1363 = derToP1363(signatureDer);

    // Vérifier la signature
    const isValid = await crypto.subtle.verify(
      {
        name: "ECDSA",
        hash: "SHA-256",
      },
      publicKey,
      signatureP1363.slice(),
      emailData
    );

    return isValid ? email : null;
  } catch (error) {
    console.error("Error verifying email signature:", error);
    return null;
  }
};

const SuccessScreen: React.FC = () => (
  <SuccessContainer>
    <SuccessTitle>
      Félicitations ! Votre compte a été créé avec succès.
    </SuccessTitle>
    <SuccessMessage>
      Votre inscription est terminée. Téléchargez le logiciel Cantoo Scribe pour
      ranger et adapter tous vos documents et manuels. Faites vos demandes de
      manuels adaptés et recevez vos livres dans les 48h.
    </SuccessMessage>
    <ActionButtonsContainer>
      <ActionButton
        href="https://cantoo.fr/app"
        target="_blank"
        rel="noopener noreferrer"
        className="primary"
      >
        Télécharger Cantoo Scribe
      </ActionButton>
      <ActionButton href="/" target="_blank" className="secondary">
        Demandez vos livres adaptés
      </ActionButton>
    </ActionButtonsContainer>
  </SuccessContainer>
);

interface RegistrationFormProps {
  formData: FormData;
  errors: Partial<Record<keyof FormData | "general", string>>;
  isSubmitting: boolean;
  onInputChange: (
    field: keyof FormData
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: React.FormEvent) => Promise<void>;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  formData,
  errors,
  isSubmitting,
  onInputChange,
  onSubmit,
}) => (
  <>
    <WelcomeMessage>
      <p>
        <strong>Bonjour,</strong>
      </p>
      <p>Cette page est réservée aux clients : Le monde de Cléo.</p>
      <p>
        Cette page vous permettra d'exploiter vos manuels numériques grâce à un
        partenariat avec Cantoo et Bookin. Vous pourrez demander vos manuels
        adaptés et les recevoir dans les 48h.
      </p>
      <p>
        Une fois le formulaire suivant rempli, vous allez pouvoir télécharger le
        logiciel Cantoo Scribe qui vous permettra d'exploiter vos manuels, et
        demander vos manuels numériques à partir de leur code barre. Vous les
        recevrez sous 48h.
      </p>
    </WelcomeMessage>

    <StyledForm onSubmit={onSubmit}>
      <FieldWrapper>
        Je suis*
        <RadioWrapper>
          <RadioOption>
            <RadioInput
              type="radio"
              name="userType"
              value="parent"
              checked={formData.userType === "parent"}
              onChange={onInputChange("userType")}
              required
            />
            le parent ou le responsable de l'enfant
          </RadioOption>
          <RadioOption>
            <RadioInput
              type="radio"
              name="userType"
              value="other"
              checked={formData.userType === "other"}
              onChange={onInputChange("userType")}
            />
            autre
          </RadioOption>
        </RadioWrapper>
        {errors.userType && <ErrorMessage>{errors.userType}</ErrorMessage>}
      </FieldWrapper>

      <FieldWrapper>
        Prénom*
        <TextInput
          type="text"
          value={formData.firstname}
          onChange={onInputChange("firstname")}
          required
        />
        {errors.firstname && <ErrorMessage>{errors.firstname}</ErrorMessage>}
      </FieldWrapper>

      <FieldWrapper>
        Nom*
        <TextInput
          type="text"
          value={formData.lastname}
          onChange={onInputChange("lastname")}
          required
        />
        {errors.lastname && <ErrorMessage>{errors.lastname}</ErrorMessage>}
      </FieldWrapper>

      <FieldWrapper>
        Adresse email*
        <TextInput
          type="email"
          value={formData.email}
          onChange={onInputChange("email")}
          disabled={true}
          required
        />
        <InfoMessage>
          Adresse email pré-remplie et vérifiée automatiquement.
        </InfoMessage>
        {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
      </FieldWrapper>

      <FieldWrapper>
        Mot de passe*
        <TextInput
          type="password"
          value={formData.password}
          onChange={onInputChange("password")}
          required
        />
        {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
      </FieldWrapper>

      <FieldWrapper>
        Confirmez le mot de passe*
        <TextInput
          type="password"
          value={formData.confirmPassword}
          onChange={onInputChange("confirmPassword")}
          required
        />
        {errors.confirmPassword && (
          <ErrorMessage>{errors.confirmPassword}</ErrorMessage>
        )}
      </FieldWrapper>

      <FieldWrapper>
        Téléphone
        <TextInput
          type="tel"
          value={formData.phone}
          onChange={onInputChange("phone")}
          placeholder="0123456789 ou 33123456789"
        />
        <InfoMessage>
          Recommandé. Cela ne sera utilisé qu'en cas de problème avec votre
          compte ou à des fins liées au service.
        </InfoMessage>
        {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
      </FieldWrapper>

      <FieldWrapper>
        Code Postal*
        <TextInput
          type="text"
          value={formData.postalCode}
          onChange={onInputChange("postalCode")}
          pattern="\d{5}"
          required
        />
        {errors.postalCode && <ErrorMessage>{errors.postalCode}</ErrorMessage>}
      </FieldWrapper>

      <CheckboxWrapper>
        <CheckboxInput
          type="checkbox"
          checked={formData.acceptDataTreatment}
          onChange={onInputChange("acceptDataTreatment")}
          required
        />
        <CheckboxText>
          J'accepte que mes données soient traitées par Cantoo et Bookin afin de
          m'offrir leurs services. Elles seront conservées, au plus, un an après
          la dernière utilisation du service{" "}
          <a
            href="https://www.cantoo.fr/legal/regulations?tab=privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            (Politique de Confidentialité)
          </a>
          .*
        </CheckboxText>
      </CheckboxWrapper>
      {errors.acceptDataTreatment && (
        <ErrorMessage>{errors.acceptDataTreatment}</ErrorMessage>
      )}

      <CheckboxWrapper>
        <CheckboxInput
          type="checkbox"
          checked={formData.engagement}
          onChange={onInputChange("engagement")}
          required
        />
        <CheckboxText>
          J'atteste sur l'honneur être bénéficiaire de{" "}
          <a
            href="https://www.legifrance.gouv.fr/codes/section_lc/LEGITEXT000006069414/LEGISCTA000019989918/2009-09-23"
            target="_blank"
            rel="noopener noreferrer"
          >
            l'excetion handicap au droit d'auteur
          </a>
          .*
        </CheckboxText>
      </CheckboxWrapper>
      {errors.engagement && (
        <ErrorMessage>{errors.engagement}</ErrorMessage>
      )}

      {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}

      <ButtonContainer>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Spinner />}
          {isSubmitting ? "Création du compte..." : "Créer mon compte"}
        </Button>
      </ButtonContainer>
    </StyledForm>
  </>
);

const CleoForm = () => {
  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    userType: "",
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    postalCode: "",
    acceptDataTreatment: false,
    engagement: false,
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof FormData | "general", string>>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasValidEmail, setHasValidEmail] = useState(false);

  // Effect to check for signed email in query string
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const signedEmail = urlParams.get("email");

    if (signedEmail) {
      verifyEmailSignature(signedEmail).then((email) => {
        if (email) {
          setFormData((prev) => ({ ...prev, email }));
          setHasValidEmail(true);
        } else {
          setErrors({
            general: "Lien invalide ou expiré. Veuillez contacter le support.",
          });
        }
      });
    } else {
      setErrors({
        general:
          "Accès non autorisé. Vous devez utiliser un lien valide pour accéder à cette page.",
      });
    }
  }, []);

  const handleInputChange = useCallback(
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }

      // Special validation for userType - show error immediately if "other" is selected
      if (field === "userType" && value === "other") {
        setErrors((prev) => ({
          ...prev,
          userType:
            "Seul l'enfant concerné ou son responsable légal peuvent bénéficier de ce programme",
        }));
      }
    },
    [errors]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof FormData | "general", string>> = {};

    // Check user type
    if (!formData.userType) {
      newErrors.userType = "Veuillez sélectionner votre statut";
    } else if (formData.userType === "other") {
      newErrors.userType =
        "Seul l'enfant concerné ou son responsable légal peuvent bénéficier de ce programme";
    }

    // Check required fields
    if (!formData.firstname.trim()) {
      newErrors.firstname = "Le prénom est obligatoire";
    }

    if (!formData.lastname.trim()) {
      newErrors.lastname = "Le nom est obligatoire";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'adresse email est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "L'adresse email n'est pas valide";
    }

    // Check passwords
    if (!formData.password) {
      newErrors.password = "Le mot de passe est obligatoire";
    } else if (formData.password.length <= 4) {
      newErrors.password = "Le mot de passe doit contenir plus de 4 caractères";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Veuillez confirmer votre mot de passe";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    }

    // Check postal code (should be 5 digits for France)
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = "Le code postal est obligatoire";
    } else if (!/^\d{5}$/.test(formData.postalCode)) {
      newErrors.postalCode = "Le code postal doit contenir 5 chiffres";
    }

    // Check phone number if provided
    if (formData.phone.trim()) {
      const cleanPhone = formData.phone.replace(/\D/g, "");
      if (
        !(
          (cleanPhone.length === 10 && cleanPhone.startsWith("0")) ||
          (cleanPhone.length === 11 && cleanPhone.startsWith("33"))
        )
      ) {
        newErrors.phone =
          "Le numéro de téléphone doit contenir 10 chiffres (commençant par 0) ou 11 chiffres (commençant par 33)";
      }
    }

    // Check data treatment acceptance
    if (!formData.acceptDataTreatment) {
      newErrors.acceptDataTreatment =
        "Vous devez accepter le traitement des données pour continuer";
    }

    // Check engagement
    if (!formData.engagement) {
      newErrors.engagement =
        "Vous devez être bénéficiaire de l'exception au droit d'auteur pour continuer";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);
      setErrors({});

      try {
        // Clean phone number
        const cleanPhone = formData.phone.replace(/\D/g, "");

        // Submit to our server which will handle Cantoo registration and local user creation
        const response = await fetch("/api/register-cleo", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstname: formData.firstname.trim(),
            lastname: formData.lastname.trim(),
            email: formData.email.toLowerCase().trim(),
            password: formData.password,
            phone: cleanPhone,
            postalCode: formData.postalCode,
          }),
        });

        if (response.status === 400) {
          const errorData = await response.json();
          console.error("Registration error:", errorData);
          setErrors({
            general:
              errorData.message ||
              "Un champ est erroné. Veuillez vérifier vos informations.",
          });
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          setErrors({
            general:
              errorData.message ||
              "Une erreur inconnue est survenue. Veuillez nous contacter sur https://cantoo.fr/contact pour signaler le problème.",
          });
          return;
        }

        // Registration successful
        setIsRegistrationComplete(true);
        const registeredEmail = formData.email.toLowerCase().trim();
        localStorage.setItem("email", registeredEmail);
      } catch (error) {
        console.error("Registration error:", error);
        setErrors({
          general:
            "Une erreur inconnue est survenue. Veuillez nous contacter sur https://cantoo.fr/contact pour signaler le problème.",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm]
  );

  return (
    <Container>
      <Header>
        <HeaderLogo src="assets/bookin.svg" alt="Bookin Logo" />
      </Header>
      <Wrapper>
        {isRegistrationComplete ? (
          <SuccessScreen />
        ) : hasValidEmail ? (
          <RegistrationForm
            formData={formData}
            errors={errors}
            isSubmitting={isSubmitting}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "2em" }}>
            <h2 style={{ color: "#dc3545", marginBottom: "1em" }}>
              Accès refusé
            </h2>
            {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}
          </div>
        )}
      </Wrapper>
      <Footer>
        <FooterText>Système automatisé développé et offert par</FooterText>
        <a href="https://cantoo.fr" target="_blank" rel="noopener noreferrer">
          <FooterLogo src="assets/cantoo.svg" alt="Cantoo Logo" />
        </a>
      </Footer>
    </Container>
  );
};

export default CleoForm;
